// Kinovea-style motion analyzer, fully in-browser.
//
// FLUJO: el coach abre el vídeo original y puede preparar el análisis ANTES de
// grabar (seguir la barra, dibujar, medir ángulos). Al pulsar "Start recording"
// se captura todo lo que hay en el canvas —incluido lo ya dibujado y la
// trayectoria ya trazada— con la marca de agua (logo abajo-izquierda,
// incredibilis.fitness abajo-derecha). El micrófono se activa al grabar.
// Al parar, la grabación queda PENDIENTE y el coach decide si la guarda en la
// aplicación (Supabase) o la descarta. Aquí no se descarga nada: la descarga se
// hace luego desde la lista de correcciones.
import React, { useEffect, useRef, useState } from "react";
import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  MicrophoneIcon,
  SunIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { toGray, stretchGray, computeStretchLUT, applyLUT } from "./imageProc";
import { createTracker, stepTracker } from "./barTracker";

const WASM_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task";
const MAX_WORK_DIM = 1280;
// Misma pila de fuentes que la web (Tailwind `font-sans` por defecto, que es lo
// que usa la app: no hay familia personalizada en tailwind.config.js).
const SITE_FONT =
  'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';
const FRAME_STEP = 1 / 30;
const BAR_COLOR = "#f97316"; // naranja: trayectoria del disco de la barra
// Puntos del esqueleto a los que se puede enganchar un seguimiento (índices de
// MediaPipe Pose). Se excluyen los de la cara: no aportan al análisis técnico.
const LANDMARK_NAMES = {
  11: "left shoulder",
  12: "right shoulder",
  13: "left elbow",
  14: "right elbow",
  15: "left wrist",
  16: "right wrist",
  23: "left hip",
  24: "right hip",
  25: "left knee",
  26: "right knee",
  27: "left ankle",
  28: "right ankle",
  29: "left heel",
  30: "right heel",
  31: "left foot",
  32: "right foot",
};

function formatTime(s) {
  if (!Number.isFinite(s)) return "0:00.0";
  const m = Math.floor(s / 60);
  const sec = (s - m * 60).toFixed(1).padStart(4, "0");
  return `${m}:${sec}`;
}

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function angleDeg(a, b, c) {
  const v1 = { x: a.x - b.x, y: a.y - b.y };
  const v2 = { x: c.x - b.x, y: c.y - b.y };
  const dot = v1.x * v2.x + v1.y * v2.y;
  const n = Math.hypot(v1.x, v1.y) * Math.hypot(v2.x, v2.y);
  if (n < 1e-6) return 0;
  return (Math.acos(Math.min(1, Math.max(-1, dot / n))) * 180) / Math.PI;
}

function distToSegment(p, a, b) {
  const l2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  if (l2 < 1e-6) return dist(p, a);
  let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  return dist(p, { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) });
}

function pickMimeType() {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  const candidates = [
    "video/mp4;codecs=avc1",
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm",
  ];
  return candidates.find((m) => window.MediaRecorder.isTypeSupported(m)) || null;
}

const btnSecondary =
  "inline-flex items-center gap-1 rounded-md bg-white px-2 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40";
const btnActive =
  "inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-1.5 text-sm font-semibold text-white shadow-sm";
const btnPrimary =
  "inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:bg-gray-300";

function IconBtn({ title, onClick, active, disabled, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={active ? btnActive : btnSecondary}
    >
      {children}
    </button>
  );
}

function MotionAnalyzer({ src, onSaveCorrection }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const logoRef = useRef(null);
  const cancelFrameRef = useRef(null);

  const enhanceRef = useRef(true); // sólo afecta a lo que se ve y se graba
  const skeletonRef = useRef(false);
  const poseRef = useRef({ landmarker: null });
  const poseResultRef = useRef(null);
  const tsRef = useRef(0);

  // Todos los seguimientos activos: la barra y los puntos del cuerpo (rodilla,
  // cadera...). Cada uno: { id, kind, color, tracker, path, active }.
  // path lleva null como corte cuando el punto se pierde y se recupera.
  const tracksRef = useRef([]);
  const trackingRef = useRef(false);
  const trackIdRef = useRef(1);

  const objectsRef = useRef([]); // {id, type, color, width, pts:[{x,y}...]}
  const draftRef = useRef(null);
  const angleDraftRef = useRef([]);
  const dragRef = useRef(null);
  const selectedIdRef = useRef(null);
  const idSeqRef = useRef(1);
  const colorRef = useRef("#22d3ee");
  const widthRef = useRef(4);

  const recordingRef = useRef(false);
  const recorderRef = useRef(null);
  const micStreamRef = useRef(null);
  const pendingUrlRef = useRef(null);

  const [videoMeta, setVideoMeta] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [enhance, setEnhance] = useState(true);
  const [skeletonOn, setSkeletonOn] = useState(false);
  const [poseLoading, setPoseLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [micAvailable, setMicAvailable] = useState(false);
  const [tool, setTool] = useState("select");
  const [trackingUI, setTrackingUI] = useState(false);
  const [trackCount, setTrackCount] = useState(0);
  const [recording, setRecording] = useState(false);
  const [pending, setPending] = useState(null); // { blob, ext, url }
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedIdState] = useState(null);
  const [color, setColor] = useState("#22d3ee");
  const [width, setWidth] = useState(4);
  const [status, setStatus] = useState("");

  const setSelected = (id) => {
    selectedIdRef.current = id;
    setSelectedIdState(id);
  };

  useEffect(() => {
    const img = new Image();
    img.src = "/images/logo.png";
    logoRef.current = img;
    const pose = poseRef.current;
    return () => {
      if (cancelFrameRef.current) cancelFrameRef.current();
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
      if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());
      if (pendingUrlRef.current) URL.revokeObjectURL(pendingUrlRef.current);
      if (pose.landmarker) pose.landmarker.close();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (selectedIdRef.current != null) {
        objectsRef.current = objectsRef.current.filter((o) => o.id !== selectedIdRef.current);
        setSelected(null);
        renderSoon();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------- drawing

  function drawWatermark(ctx, W, H) {
    const pad = Math.round(W * 0.02);
    const fs = Math.max(14, Math.round(W * 0.024)) + 2;
    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.font = `bold ${fs}px ${SITE_FONT}`;
    ctx.textBaseline = "bottom";
    ctx.shadowColor = "rgba(0,0,0,0.7)";
    ctx.shadowBlur = 4;
    ctx.fillStyle = "#ffffff";
    const img = logoRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      const lw = Math.round(W * 0.3);
      const lh = Math.round((lw * img.naturalHeight) / img.naturalWidth);
      ctx.drawImage(img, pad, H - pad - lh, lw, lh); // logo abajo-izquierda
    }
    ctx.textAlign = "right";
    ctx.fillText("incredibilis.fitness", W - pad, H - pad); // dominio abajo-derecha
    ctx.restore();
  }

  function drawSkeleton(ctx, W, H) {
    const res = poseResultRef.current;
    const lm = res && res.landmarks && res.landmarks[0];
    if (!lm) return;
    ctx.save();
    ctx.lineWidth = Math.max(2, W * 0.004);
    ctx.strokeStyle = "rgba(74,222,128,0.9)";
    for (const c of PoseLandmarker.POSE_CONNECTIONS) {
      const a = lm[c.start];
      const b = lm[c.end];
      if (!a || !b) continue;
      if ((a.visibility ?? 1) < 0.4 || (b.visibility ?? 1) < 0.4) continue;
      ctx.beginPath();
      ctx.moveTo(a.x * W, a.y * H);
      ctx.lineTo(b.x * W, b.y * H);
      ctx.stroke();
    }
    ctx.fillStyle = "#ffffff";
    for (const p of lm) {
      if ((p.visibility ?? 1) < 0.4) continue;
      ctx.beginPath();
      ctx.arc(p.x * W, p.y * H, Math.max(2, W * 0.003), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  function drawArrowHead(ctx, from, to, size) {
    const ang = Math.atan2(to.y - from.y, to.x - from.x);
    ctx.beginPath();
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(to.x - size * Math.cos(ang - 0.45), to.y - size * Math.sin(ang - 0.45));
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(to.x - size * Math.cos(ang + 0.45), to.y - size * Math.sin(ang + 0.45));
    ctx.stroke();
  }

  function drawObject(ctx, obj, W, isSelected) {
    const [a, b, c] = obj.pts;
    ctx.save();
    ctx.strokeStyle = obj.color;
    ctx.fillStyle = obj.color;
    ctx.lineWidth = obj.width;
    ctx.lineCap = "round";
    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowBlur = 2;
    if (obj.type === "line" || obj.type === "arrow") {
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      if (obj.type === "arrow") drawArrowHead(ctx, a, b, Math.max(12, obj.width * 3.5));
    } else if (obj.type === "circle") {
      ctx.beginPath();
      ctx.arc(a.x, a.y, dist(a, b), 0, Math.PI * 2);
      ctx.stroke();
    } else if (obj.type === "angle") {
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.stroke();
      const fs = Math.max(12, Math.round(W * 0.02));
      ctx.font = `bold ${fs}px sans-serif`;
      ctx.fillText(`${angleDeg(a, b, c).toFixed(0)}°`, b.x + fs * 0.6, b.y - fs * 0.4);
    }
    if (isSelected) {
      ctx.shadowBlur = 0;
      for (const p of obj.pts) {
        ctx.beginPath();
        ctx.rect(p.x - 5, p.y - 5, 10, 10);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "#111827";
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // Cada trayectoria puede tener huecos (oclusiones): se dibuja por tramos para
  // no unir con una recta falsa el punto previo y el de reaparición.
  function drawTrack(ctx, track, W, t) {
    const { path, color } = track;
    if (path.length === 0) return;
    const segments = [];
    let cur = [];
    for (const p of path) {
      if (p === null) {
        if (cur.length) segments.push(cur);
        cur = [];
      } else cur.push(p);
    }
    if (cur.length) segments.push(cur);

    ctx.save();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    const stroke = (pts, alpha, lw) => {
      if (pts.length < 2) return;
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = color;
      ctx.lineWidth = lw;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    };
    let last = null;
    for (const seg of segments) {
      stroke(seg, 0.35, Math.max(1.5, W * 0.002)); // trayectoria completa, tenue
      const done = seg.filter((p) => p.t <= t + 1e-3);
      stroke(done, 0.95, Math.max(2.5, W * 0.0035)); // recorrido hasta el frame
      if (done.length) last = done[done.length - 1];
    }
    if (last) {
      ctx.globalAlpha = 0.95;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(last.x, last.y, Math.max(4, W * 0.006), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  function drawOverlays(ctx, W, H, t) {
    if (skeletonRef.current) drawSkeleton(ctx, W, H);
    for (const track of tracksRef.current) drawTrack(ctx, track, W, t);
    for (const obj of objectsRef.current) {
      drawObject(ctx, obj, W, obj.id === selectedIdRef.current);
    }
    if (draftRef.current) drawObject(ctx, draftRef.current, W, false);
    if (angleDraftRef.current.length > 0) {
      ctx.save();
      ctx.fillStyle = colorRef.current;
      for (const p of angleDraftRef.current) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(3, W * 0.004), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
    if (recordingRef.current) drawWatermark(ctx, W, H);
  }

  // ---------------------------------------------------------------- pipeline

  function stopTracking(msg) {
    for (const t of tracksRef.current) t.active = false;
    trackingRef.current = false;
    setTrackingUI(false);
    if (msg) setStatus(msg);
  }

  function renderFrame(mediaTime) {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    const W = c.width;
    const H = c.height;
    ctx.drawImage(v, 0, 0, W, H);

    let frame = null;
    if (enhanceRef.current || trackingRef.current) {
      frame = ctx.getImageData(0, 0, W, H);
    }
    // El tracker parte SIEMPRE del frame crudo (toGray copia los píxeles, así
    // que la LUT posterior no le afecta): no depende del ajuste visual.
    let gray = null;
    const needTemplate = tracksRef.current.some((tk) => tk.active && tk.kind === "bar");
    if (needTemplate && frame) gray = stretchGray(toGray(frame));
    if (enhanceRef.current && frame) {
      applyLUT(frame, computeStretchLUT(frame));
      ctx.putImageData(frame, 0, 0);
    }

    // La pose se calcula si se muestra el esqueleto o si hay puntos del cuerpo
    // en seguimiento (esos puntos SON landmarks del modelo).
    const needPose =
      skeletonRef.current ||
      tracksRef.current.some((tk) => tk.active && tk.kind === "landmark");
    if (needPose && poseRef.current.landmarker) {
      const ts = Math.max(performance.now(), tsRef.current + 1);
      tsRef.current = ts;
      try {
        poseResultRef.current = poseRef.current.landmarker.detectForVideo(c, ts);
      } catch (err) {
        // frame no procesable; se mantiene el último resultado
      }
    }

    if (trackingRef.current) {
      const landmarks = poseResultRef.current?.landmarks?.[0] || null;
      for (const track of tracksRef.current) {
        if (!track.active) continue;
        const path = track.path;
        if (track.kind === "landmark") {
          // El punto del cuerpo se lee del modelo de pose en cada frame: no
          // deriva y se recupera solo tras una oclusión.
          const lm = landmarks && landmarks[track.landmarkIndex];
          if (lm && (lm.visibility ?? 1) >= 0.4) {
            path.push({ t: mediaTime, x: lm.x * W, y: lm.y * H });
          } else if (path.length && path[path.length - 1] !== null) {
            path.push(null); // sin visibilidad: se corta el trazo
          }
        } else if (gray) {
          // La barra se sigue por plantilla (NCC); si desaparece, su tracker
          // sigue buscando y la reengancha al reaparecer.
          const res = stepTracker(track.tracker, gray);
          if (res.ok) {
            if (res.reacquired && path.length && path[path.length - 1] !== null) path.push(null);
            path.push({ t: mediaTime, x: res.x, y: res.y });
          }
        }
      }
    }

    drawOverlays(ctx, W, H, mediaTime);
  }

  function scheduleNext() {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestVideoFrameCallback) {
      const id = v.requestVideoFrameCallback((now, meta) => tick(meta.mediaTime));
      cancelFrameRef.current = () => v.cancelVideoFrameCallback(id);
    } else {
      const id = requestAnimationFrame(() => tick(v.currentTime));
      cancelFrameRef.current = () => cancelAnimationFrame(id);
    }
  }

  function tick(mediaTime) {
    renderFrame(mediaTime);
    const v = videoRef.current;
    if (v && !v.paused && !v.ended) scheduleNext();
  }

  function renderSoon() {
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (v) renderFrame(v.currentTime);
    });
  }

  // ---------------------------------------------------------------- video

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const scale = Math.min(1, MAX_WORK_DIM / Math.max(v.videoWidth, v.videoHeight));
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    setVideoMeta({ duration: v.duration });
    renderSoon();
  };

  const handlePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) v.play();
    else v.pause();
  };

  const stepFrame = (dir) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = Math.min(Math.max(0, v.currentTime + dir * FRAME_STEP), v.duration || 0);
  };

  const handleMuteToggle = () => {
    const v = videoRef.current;
    const next = !muted;
    setMuted(next);
    if (v) v.muted = next;
  };

  const handleEnhanceToggle = () => {
    const next = !enhance;
    enhanceRef.current = next;
    setEnhance(next);
    renderSoon();
  };

  // Carga (una vez) el modelo de pose. Lo usan el esqueleto y el seguimiento de
  // puntos del cuerpo. Devuelve el landmarker o null si no se pudo cargar.
  const ensurePoseModel = async () => {
    if (poseRef.current.landmarker) return poseRef.current.landmarker;
    setPoseLoading(true);
    setStatus("Loading the pose model…");
    try {
      const vision = await FilesetResolver.forVisionTasks(WASM_URL);
      poseRef.current.landmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.3,
        minPosePresenceConfidence: 0.3,
        minTrackingConfidence: 0.3,
      });
      setStatus("");
      return poseRef.current.landmarker;
    } catch (err) {
      setStatus("Could not load the pose model (check your connection).");
      return null;
    } finally {
      setPoseLoading(false);
    }
  };

  const handleSkeletonToggle = async () => {
    const next = !skeletonOn;
    skeletonRef.current = next;
    setSkeletonOn(next);
    if (!next) {
      poseResultRef.current = null;
      renderSoon();
      return;
    }
    const ok = await ensurePoseModel();
    if (!ok) {
      skeletonRef.current = false;
      setSkeletonOn(false);
    }
    renderSoon();
  };

  // ---------------------------------------------------------------- tools

  const selectTool = (t) => {
    draftRef.current = null;
    angleDraftRef.current = [];
    setTool(t);
    if (t === "bar") setStatus("Click the CENTER of the plate to start tracking it.");
    else if (t === "point")
      setStatus(
        "Click near the body point to follow (knee, hip…): it snaps to the athlete's skeleton and uses the selected color."
      );
    else if (t === "line") setStatus("Drag to draw a line.");
    else if (t === "arrow") setStatus("Drag to draw an arrow.");
    else if (t === "circle") setStatus("Drag from the center to draw a circle.");
    else if (t === "angle") setStatus("Click 3 points (the 2nd is the vertex).");
    else setStatus("");
    renderSoon();
  };

  // El canvas se muestra con object-contain: hay que descontar el letterbox.
  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const scale = Math.min(rect.width / c.width, rect.height / c.height) || 1;
    const offX = rect.left + (rect.width - c.width * scale) / 2;
    const offY = rect.top + (rect.height - c.height * scale) / 2;
    return {
      x: Math.max(0, Math.min(c.width, (e.clientX - offX) / scale)),
      y: Math.max(0, Math.min(c.height, (e.clientY - offY) / scale)),
    };
  };

  const hitHandle = (obj, p) => {
    for (let i = 0; i < obj.pts.length; i++) if (dist(obj.pts[i], p) < 12) return i;
    return -1;
  };

  const hitObject = (p) => {
    const objs = objectsRef.current;
    for (let i = objs.length - 1; i >= 0; i--) {
      const o = objs[i];
      const tol = Math.max(10, o.width + 6);
      if (o.type === "line" || o.type === "arrow") {
        if (distToSegment(p, o.pts[0], o.pts[1]) < tol) return o;
      } else if (o.type === "circle") {
        if (Math.abs(dist(p, o.pts[0]) - dist(o.pts[0], o.pts[1])) < tol) return o;
      } else if (o.type === "angle") {
        if (
          distToSegment(p, o.pts[0], o.pts[1]) < tol ||
          distToSegment(p, o.pts[1], o.pts[2]) < tol
        )
          return o;
      }
    }
    return null;
  };

  const handlePointerDown = (e) => {
    if (!videoMeta) return;
    const pos = getPos(e);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (tool === "bar" || tool === "point") {
      dragRef.current = { mode: "trackClick", kind: tool === "bar" ? "bar" : "point" };
    } else if (tool === "line" || tool === "arrow" || tool === "circle") {
      draftRef.current = {
        id: idSeqRef.current,
        type: tool,
        color: colorRef.current,
        width: widthRef.current,
        pts: [pos, { ...pos }],
      };
      dragRef.current = { mode: "create" };
    } else if (tool === "angle") {
      dragRef.current = { mode: "anglePoint" };
    } else {
      const sel = objectsRef.current.find((o) => o.id === selectedIdRef.current);
      if (sel) {
        const hi = hitHandle(sel, pos);
        if (hi >= 0) {
          dragRef.current = { mode: "handle", idx: hi };
          return;
        }
      }
      const obj = hitObject(pos);
      if (obj) {
        setSelected(obj.id);
        dragRef.current = { mode: "move", last: pos };
      } else setSelected(null);
    }
    renderSoon();
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const pos = getPos(e);
    if (drag.mode === "create") {
      draftRef.current.pts[1] = pos;
    } else if (drag.mode === "handle") {
      const sel = objectsRef.current.find((o) => o.id === selectedIdRef.current);
      if (sel) sel.pts[drag.idx] = pos;
    } else if (drag.mode === "move") {
      const sel = objectsRef.current.find((o) => o.id === selectedIdRef.current);
      if (sel) {
        const dx = pos.x - drag.last.x;
        const dy = pos.y - drag.last.y;
        sel.pts = sel.pts.map((p) => ({ x: p.x + dx, y: p.y + dy }));
        drag.last = pos;
      }
    }
    renderSoon();
  };

  const handlePointerUp = (e) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    const pos = getPos(e);
    if (drag.mode === "create") {
      const draft = draftRef.current;
      draftRef.current = null;
      if (draft && dist(draft.pts[0], draft.pts[1]) > 6) {
        objectsRef.current.push(draft);
        idSeqRef.current += 1;
        setSelected(draft.id);
        setTool("select");
        setStatus("");
      }
    } else if (drag.mode === "trackClick") {
      seedTrack(drag.kind, pos);
    } else if (drag.mode === "anglePoint") {
      angleDraftRef.current.push(pos);
      if (angleDraftRef.current.length === 3) {
        objectsRef.current.push({
          id: idSeqRef.current,
          type: "angle",
          color: colorRef.current,
          width: widthRef.current,
          pts: angleDraftRef.current,
        });
        setSelected(idSeqRef.current);
        idSeqRef.current += 1;
        angleDraftRef.current = [];
        setTool("select");
        setStatus("");
      }
    }
    renderSoon();
  };

  const handleColorChange = (e) => {
    const value = e.target.value;
    setColor(value);
    colorRef.current = value;
    const sel = objectsRef.current.find((o) => o.id === selectedIdRef.current);
    if (sel) {
      sel.color = value;
      renderSoon();
    }
  };

  const handleWidthChange = (e) => {
    const value = Number(e.target.value);
    setWidth(value);
    widthRef.current = value;
    const sel = objectsRef.current.find((o) => o.id === selectedIdRef.current);
    if (sel) {
      sel.width = value;
      renderSoon();
    }
  };

  const deleteSelected = () => {
    if (selectedIdRef.current == null) return;
    objectsRef.current = objectsRef.current.filter((o) => o.id !== selectedIdRef.current);
    setSelected(null);
    renderSoon();
  };

  const clearTracks = () => {
    tracksRef.current = [];
    setTrackCount(0);
    stopTracking();
    setStatus("Tracked paths cleared.");
    renderSoon();
  };

  const pushTrack = (track) => {
    tracksRef.current.push(track);
    setTrackCount(tracksRef.current.length);
    trackingRef.current = true;
    setTrackingUI(true);
    setTool("select");
    renderSoon();
  };

  // Seguimiento del disco de la barra: plantilla NCC sembrada en el clic.
  const seedBarTrack = (center) => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, c.width, c.height);
    const frame = ctx.getImageData(0, 0, c.width, c.height);
    const gray = stretchGray(toGray(frame)); // mismo preprocesado que renderFrame
    const tracker = createTracker(gray, center.x, center.y);
    if (enhanceRef.current) {
      applyLUT(frame, computeStretchLUT(frame));
      ctx.putImageData(frame, 0, 0);
    }
    pushTrack({
      id: trackIdRef.current++,
      kind: "bar",
      color: BAR_COLOR,
      tracker,
      path: [{ t: v.currentTime, x: center.x, y: center.y }],
      active: true,
    });
    setStatus("Tracking the plate. It keeps searching if it goes out of sight and picks it up again.");
  };

  // Seguimiento de un punto del cuerpo: se engancha al landmark de pose MÁS
  // CERCANO al clic y a partir de ahí se lee del modelo en cada frame. Seguir
  // píxeles por plantilla no sirve aquí (la piel/malla apenas tiene textura y
  // deforma: en pruebas se enganchaba al rack del fondo o derivaba).
  const seedPointTrack = async (pos) => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const landmarker = await ensurePoseModel();
    if (!landmarker) return;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, c.width, c.height);
    if (enhanceRef.current) {
      const frame = ctx.getImageData(0, 0, c.width, c.height);
      applyLUT(frame, computeStretchLUT(frame));
      ctx.putImageData(frame, 0, 0);
    }
    const ts = Math.max(performance.now(), tsRef.current + 1);
    tsRef.current = ts;
    let result = null;
    try {
      result = landmarker.detectForVideo(c, ts);
    } catch (err) {
      result = null;
    }
    poseResultRef.current = result;
    const landmarks = result?.landmarks?.[0];
    if (!landmarks) {
      setStatus("No athlete detected in this frame — try another frame.");
      renderSoon();
      return;
    }
    let best = null;
    for (const idx of Object.keys(LANDMARK_NAMES)) {
      const lm = landmarks[idx];
      if (!lm || (lm.visibility ?? 1) < 0.4) continue;
      const d = Math.hypot(lm.x * c.width - pos.x, lm.y * c.height - pos.y);
      if (!best || d < best.d) best = { d, idx: Number(idx), lm };
    }
    if (!best) {
      setStatus("No body point detected near that click — try another frame.");
      renderSoon();
      return;
    }
    pushTrack({
      id: trackIdRef.current++,
      kind: "landmark",
      landmarkIndex: best.idx,
      color: colorRef.current,
      path: [{ t: v.currentTime, x: best.lm.x * c.width, y: best.lm.y * c.height }],
      active: true,
    });
    setStatus(`Following the ${LANDMARK_NAMES[best.idx]}. Mark more points to follow several at once.`);
  };

  const seedTrack = (kind, center) => {
    if (kind === "bar") seedBarTrack(center);
    else seedPointTrack(center);
  };

  // ---------------------------------------------------------------- recording

  const handleStartRecording = async () => {
    const c = canvasRef.current;
    if (!c || !videoMeta || recording) return;
    const mime = pickMimeType();
    if (!mime) {
      setStatus("This browser does not support video recording (MediaRecorder).");
      return;
    }
    if (pending) discardPending();

    // El micrófono se pide al empezar (para tener su pista en la grabación) pero
    // arranca SILENCIADO: sólo se graba la voz cuando el coach pulsa el botón
    // del micro. El botón activa/silencia la pista en vivo.
    let micStream = null;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      micStreamRef.current = micStream;
      micStream.getAudioTracks().forEach((t) => {
        t.enabled = false;
      });
      setMicAvailable(true);
      setMicOn(false);
    } catch (err) {
      setMicAvailable(false);
      setMicOn(false);
    }

    const canvasStream = c.captureStream(30);
    const tracks = [...canvasStream.getVideoTracks()];
    if (micStream) tracks.push(...micStream.getAudioTracks());
    const rec = new MediaRecorder(new MediaStream(tracks), {
      mimeType: mime,
      videoBitsPerSecond: 8000000,
    });
    const chunks = [];
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };
    rec.onstop = () => {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
        micStreamRef.current = null;
      }
      recordingRef.current = false;
      setRecording(false);
      setMicAvailable(false);
      setMicOn(false);
      const ext = mime.includes("mp4") ? "mp4" : "webm";
      const blob = new Blob(chunks, { type: mime.split(";")[0] });
      const url = URL.createObjectURL(blob);
      pendingUrlRef.current = url;
      setPending({ blob, ext, url });
      setStatus("Recording finished. Review it below and save it to this video's corrections.");
      renderSoon();
    };
    recorderRef.current = rec;
    setSelected(null); // sin selección: los tiradores no salen en la grabación
    recordingRef.current = true;
    setRecording(true);
    rec.start(250);
    renderSoon();
    setStatus(
      micStream
        ? "REC — everything on screen is being recorded. Press the microphone button when you want your voice in."
        : "REC — everything on screen is being recorded (microphone unavailable)."
    );
  };

  const handleStopRecording = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    stopTracking();
  };

  const handleMicToggle = () => {
    const stream = micStreamRef.current;
    if (!stream) return;
    const next = !micOn;
    stream.getAudioTracks().forEach((t) => {
      t.enabled = next;
    });
    setMicOn(next);
  };

  const discardPending = () => {
    if (pendingUrlRef.current) URL.revokeObjectURL(pendingUrlRef.current);
    pendingUrlRef.current = null;
    setPending(null);
  };

  const handleSavePending = async () => {
    if (!pending || saving) return;
    setSaving(true);
    setStatus("Saving the correction…");
    try {
      await onSaveCorrection(pending.blob, pending.ext);
      discardPending();
      setStatus("Correction saved. You can watch or download it from the corrections list.");
    } catch (err) {
      setStatus("Could not save the correction. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardPending = () => {
    discardPending();
    setStatus("Recording discarded.");
  };

  // ---------------------------------------------------------------- render

  const toolButtons = [
    { id: "select", label: "Select", title: "Select / edit objects" },
    { id: "line", label: "Line", title: "Draw a line (drag)" },
    { id: "arrow", label: "Arrow", title: "Draw an arrow (drag)" },
    { id: "circle", label: "Circle", title: "Draw a circle (drag from its center)" },
    { id: "angle", label: "Angle", title: "Measure an angle (3 clicks, the 2nd is the vertex)" },
  ];
  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* recording bar */}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {recording ? (
          <>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
              onClick={handleStopRecording}
              title="Stop the recording"
            >
              <StopIcon className="h-5 w-5" /> Stop recording
            </button>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-600" />
              REC
            </span>
          </>
        ) : (
          <button
            type="button"
            className={btnPrimary}
            onClick={handleStartRecording}
            disabled={!videoMeta || saving}
            title="Records everything on the canvas from now on, including the tracking and drawings already on screen"
          >
            <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500" aria-hidden="true" />
            Start recording
          </button>
        )}
        <IconBtn
          title={
            !recording
              ? "Microphone (available while recording)"
              : !micAvailable
              ? "Microphone unavailable"
              : micOn
              ? "Microphone ON — your voice is being recorded"
              : "Microphone muted"
          }
          onClick={handleMicToggle}
          active={recording && micOn}
          disabled={!recording || !micAvailable}
        >
          <MicrophoneIcon className="h-5 w-5" />
        </IconBtn>
        <span className="mx-1 hidden h-5 w-px bg-gray-300 sm:block" />
        <IconBtn
          title={enhance ? "Light enhancement ON (brightens dark footage)" : "Light enhancement OFF"}
          onClick={handleEnhanceToggle}
          active={enhance}
          disabled={!videoMeta}
        >
          <SunIcon className="h-5 w-5" />
        </IconBtn>
        <IconBtn
          title={poseLoading ? "Loading skeleton model…" : "Athlete skeleton overlay"}
          onClick={handleSkeletonToggle}
          active={skeletonOn}
          disabled={!videoMeta || poseLoading}
        >
          <UserIcon className="h-5 w-5" />
        </IconBtn>
      </div>

      {/* tools bar (only usable while recording) */}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <button
          type="button"
          title="Track the bar: click the center of the plate"
          className={tool === "bar" ? btnActive : btnSecondary}
          onClick={() => selectTool(tool === "bar" ? "select" : "bar")}
          disabled={!videoMeta}
        >
          Track bar
        </button>
        <button
          type="button"
          title="Track a body point: click near a knee, a hip… — it snaps to the skeleton (uses the selected color; you can track several)"
          className={tool === "point" ? btnActive : btnSecondary}
          onClick={() => selectTool(tool === "point" ? "select" : "point")}
          disabled={!videoMeta}
        >
          Track point
        </button>
        {trackingUI && (
          <button
            type="button"
            className={btnActive}
            onClick={() => stopTracking("Tracking stopped.")}
          >
            <StopIcon className="h-5 w-5" /> Stop tracking
          </button>
        )}
        <button
          type="button"
          className={btnSecondary}
          onClick={clearTracks}
          disabled={!videoMeta || trackCount === 0}
          title="Remove every tracked path (bar and body points)"
        >
          Clear tracks
        </button>
        <span className="mx-1 hidden h-5 w-px bg-gray-300 sm:block" />
        {toolButtons.map((t) => (
          <button
            key={t.id}
            type="button"
            title={t.title}
            className={tool === t.id ? btnActive : btnSecondary}
            onClick={() => selectTool(t.id)}
            disabled={!videoMeta}
          >
            {t.label}
          </button>
        ))}
        <input
          type="color"
          value={color}
          onChange={handleColorChange}
          title="Object color"
          className="h-8 w-9 cursor-pointer rounded-md border border-gray-300 bg-white p-0.5 disabled:opacity-40"
          disabled={!videoMeta}
        />
        <select
          className="rounded-md border-gray-300 py-1.5 text-sm disabled:opacity-40"
          value={width}
          onChange={handleWidthChange}
          title="Object stroke width"
          disabled={!videoMeta}
        >
          {[2, 3, 4, 6, 8, 12].map((w) => (
            <option key={w} value={w}>
              {w}px
            </option>
          ))}
        </select>
        <button
          type="button"
          title="Delete selected object (Del)"
          className={btnSecondary}
          onClick={deleteSelected}
          disabled={selectedId == null}
        >
          Delete
        </button>
      </div>

      {/* recorded correction, pending save */}
      {pending && (
        <div className="flex shrink-0 flex-wrap items-center gap-3 rounded-md bg-indigo-50 px-3 py-2 ring-1 ring-inset ring-indigo-200">
          <video src={pending.url} controls className="h-20 rounded bg-black" />
          <span className="text-sm font-medium text-indigo-900">
            Recording ready — not saved yet.
          </span>
          <button type="button" className={btnPrimary} onClick={handleSavePending} disabled={saving}>
            {saving ? "Saving…" : "Save correction"}
          </button>
          <button
            type="button"
            className={btnSecondary}
            onClick={handleDiscardPending}
            disabled={saving}
          >
            Discard
          </button>
        </div>
      )}

      <video
        ref={videoRef}
        src={src}
        crossOrigin="anonymous"
        className="hidden"
        playsInline
        preload="auto"
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => {
          setIsPlaying(true);
          scheduleNext();
        }}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onError={() => setStatus("Could not load this video (unsupported codec or expired link).")}
        onSeeked={() => {
          const v = videoRef.current;
          if (v && v.paused) renderFrame(v.currentTime);
        }}
        onTimeUpdate={() => {
          const v = videoRef.current;
          if (v) setCurrentTime(Math.round(v.currentTime * 10) / 10);
        }}
      />
      <div className="min-h-0 flex-1">
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          className={
            "h-full w-full rounded-lg bg-black object-contain touch-none " +
            (tool !== "select" ? "cursor-crosshair" : "cursor-default")
          }
        />
      </div>
      {videoMeta && (
        <input
          type="range"
          min={0}
          max={videoMeta.duration || 0}
          step={0.01}
          value={currentTime}
          onChange={(e) => {
            const v = videoRef.current;
            if (!v) return;
            const t = parseFloat(e.target.value);
            v.currentTime = t;
            setCurrentTime(t);
          }}
          className="w-full accent-indigo-600"
        />
      )}

      {/* playback controls, below the progress bar */}
      <div className="flex shrink-0 flex-wrap items-center justify-center gap-2">
        <IconBtn title="Previous frame" onClick={() => stepFrame(-1)} disabled={!videoMeta}>
          <ChevronLeftIcon className="h-5 w-5" />
        </IconBtn>
        <IconBtn
          title={isPlaying ? "Pause" : "Play"}
          onClick={handlePlayPause}
          disabled={!videoMeta}
        >
          {isPlaying ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
        </IconBtn>
        <IconBtn title="Next frame" onClick={() => stepFrame(1)} disabled={!videoMeta}>
          <ChevronRightIcon className="h-5 w-5" />
        </IconBtn>
        <select
          className="rounded-md border-gray-300 py-1.5 text-sm"
          value={playbackRate}
          onChange={(e) => {
            const rate = parseFloat(e.target.value);
            setPlaybackRate(rate);
            if (videoRef.current) videoRef.current.playbackRate = rate;
          }}
          disabled={!videoMeta}
          title="Playback speed"
        >
          <option value={0.25}>0.25x</option>
          <option value={0.5}>0.5x</option>
          <option value={1}>1x</option>
        </select>
        <IconBtn
          title={muted ? "Unmute video" : "Mute video"}
          onClick={handleMuteToggle}
          disabled={!videoMeta}
        >
          {muted ? <SpeakerXMarkIcon className="h-5 w-5" /> : <SpeakerWaveIcon className="h-5 w-5" />}
        </IconBtn>
        <span className="text-sm tabular-nums text-gray-600">
          {formatTime(currentTime)} / {formatTime(videoMeta?.duration)}
        </span>
      </div>

      {status && <p className="shrink-0 text-sm text-indigo-700">{status}</p>}
    </div>
  );
}

export default MotionAnalyzer;
